<template>
  <!-- Hero Section -->
  <section
    class="relative py-12 px-6 overflow-hidden bg-gray-900"
  >
    <!-- Background Illustration/Image -->
    <div class="absolute inset-0 opacity-50">
      <img
        src="/images/hero.webp"
        alt="不動産店頭チラシ"
        fetchpriority="high"
        class="w-full h-full object-cover"
      />
    </div>
    <div class="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/70"></div>

    <div class="relative max-w-[1400px] mx-auto z-10 text-center">
      <h1 class="text-3xl md:text-6xl font-bold text-white mb-6 tracking-tight whitespace-pre-wrap leading-snug">
        {{ t('hero.title') }}
      </h1>
      <h2 v-if="locale === 'ja'" class="text-lg md:text-xl text-gray-200 mb-10 font-medium">
        <span>ポータルに載らない</span>
        <span class="text-2xl font-bold"> “掘り出し物件“ </span>
        <span>が見つかる検索サイト</span>
      </h2>
      <h2 v-else class="text-lg md:text-xl text-gray-200 mb-10 font-medium">
        {{ t('hero.subtitle') }}
      </h2>

      <!-- Buy/Rent Toggle -->
      <div class="inline-flex bg-white/10 backdrop-blur-md rounded-xl p-1 mb-8 border border-white/20">
        <button
          v-for="mode in ['buy', 'rent']"
          :key="mode"
          :class="[
            'px-10 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer',
            searchMode === mode ? 'bg-white text-gray-900 shadow-lg' : 'text-white hover:bg-white/10'
          ]"
          @click="searchMode = mode"
        >
          {{ t('header.' + mode) }}
        </button>
      </div>

      <!-- Quick Map Access -->
      <div class="flex justify-center">
        <a
          :href="'/' + searchMode + '/search'"
          class="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white px-8 py-3.5 rounded-2xl text-base font-bold transition-all flex items-center gap-3 shadow-xl"
        >
          <MapIcon :size="20" class="text-orange-400" />
          {{ t('search.exploreOnMap') }}
        </a>
      </div>

      <p class="my-2 uppercase text-white text-sm font-bold">Or</p>

      <!-- Single Text Box -->
      <div class="bg-white rounded-2xl shadow-2xl p-2 flex items-center mb-4 max-w-2xl mx-auto">
        <div class="flex-1">
          <SearchBox
            @select="path => navigate('/' + searchMode + '/' + path + '/search')"
          />
        </div>
      </div>

      <!-- Keyword Buttons -->
      <div class="flex flex-wrap justify-center gap-3 mb-10">
        <a
          v-for="kw in keywords"
          :key="kw.label"
          :href="kw.url"
          class="bg-white/10 hover:bg-white/20 backdrop-blur-md border border-white/20 text-white px-5 py-2.5 rounded-full text-sm font-medium transition-all"
        >
          {{ kw.label }}
        </a>
      </div>

      <!-- Prefecture Links -->
      <div class="flex flex-wrap justify-center gap-6 text-sm text-gray-300">
        <span class="font-bold text-white">{{ t('search.mainAreas') }}:</span>
        <a v-for="pref in topPrefectures" :key="pref.name" :href="pref.url" class="hover:text-white transition-colors underline underline-offset-4 decoration-white/30">
          {{ pref.name }}
        </a>
      </div>
    </div>
  </section>

  <!-- Main Content -->
  <section class="max-w-[1400px] mx-auto px-4 sm:px-6 py-16">
    <div class="flex flex-col space-y-16">
      <!-- New Listings -->
      <div>
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-3">
            <div class="w-2 h-8 bg-brand rounded-full"></div>
            <h2 class="text-xl md:text-3xl font-bold text-gray-900">{{ t('sections.newListings') }}</h2>
          </div>
          <a :href="'/' + searchMode + '/tags/latest'" class="text-brand hover:underline font-semibold flex items-center gap-1">
            {{ t('sections.viewAll') }}
          </a>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <PropertyCard
            v-for="property in latest"
            :key="property.id"
            v-bind="property"
            photo-priority="zumen"
          />
        </div>
      </div>

      <!-- Recently Updated Listings -->
      <div>
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-3">
            <div class="w-2 h-8 bg-brand rounded-full"></div>
            <h2 class="text-xl md:text-3xl font-bold text-gray-900">{{ t('sections.recentlyUpdated') }}</h2>
          </div>
          <a :href="'/' + searchMode + '/tags/recently-updated'" class="text-brand hover:underline font-semibold flex items-center gap-1">
            {{ t('sections.viewAll') }}
          </a>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <PropertyCard
            v-for="property in recentlyUpdated"
            :key="property.id"
            v-bind="property"
            photo-priority="zumen"
          />
        </div>
      </div>

      <!-- Browse By Areas -->
      <template v-if="areas.length">
        <div>
          <div class="flex items-center gap-3 mb-8">
            <div class="w-2 h-8 bg-brand rounded-full"></div>
            <h2 class="text-3xl font-bold text-gray-900">{{ t('sections.browseByAreas') }}</h2>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <a
              v-for="(child, index) in areas"
              :key="child.slug"
              class="relative overflow-hidden rounded-xl border border-gray-200 p-6 hover:shadow-xl transition-all group flex flex-col justify-end"
              :class="getBgImage(child, index) ? 'min-h-[200px] border-none' : 'bg-white hover:border-brand/20'"
              :href="'/' + child.path"
            >
              <template v-if="getBgImage(child, index)">
                <img :src="getBgImage(child, index)" class="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
              </template>

              <div class="relative z-10 flex items-center justify-between w-full">
                <div>
                  <h3 class="font-bold text-lg transition-colors" :class="getBgImage(child, index) ? 'text-white' : 'text-gray-900 group-hover:text-brand'">
                    {{ getAddressPart(child) }}
                  </h3>
                  <p v-if="typeof child.stats?.count === 'number'" class="text-sm mt-1" :class="getBgImage(child, index) ? 'text-gray-200' : 'text-gray-500'">
                    {{ t('property.n_properties', { count: child.stats?.count?.toLocaleString() }) }}
                  </p>
                </div>
                <div class="w-10 h-10 rounded-full flex items-center justify-center transition-colors" :class="getBgImage(child, index) ? 'bg-white/20 text-white backdrop-blur-sm' : 'bg-gray-50 text-gray-400 group-hover:bg-brand/10 group-hover:text-brand'">
                  <ChevronRight class="w-5 h-5" />
                </div>
              </div>
            </a>
          </div>
        </div>
      </template>

      <!-- Agents Section -->
      <div v-if="false">
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center gap-3">
            <div class="w-2 h-8 bg-brand rounded-full"></div>
            <h2 class="text-3xl font-bold text-gray-900">{{ t('property.contact_agent').split('に')[0] }}エージェント</h2>
          </div>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div v-for="agent in agents" :key="agent.id" class="bg-white rounded-2xl border border-gray-200 p-8 text-center hover:shadow-xl transition-all group">
            <div class="relative w-32 h-32 mx-auto mb-6">
              <img :src="agent.avatar" :alt="agent.name" class="w-full h-full object-cover rounded-full border-4 border-white shadow-lg" />
              <div class="absolute bottom-1 right-1 w-6 h-6 bg-green-500 border-4 border-white rounded-full"></div>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-1 group-hover:text-brand transition-colors">{{ agent.name }}</h3>
            <p class="text-sm text-gray-500 mb-6">{{ agent.company }}</p>
            <Button variant="outline" class="w-full rounded-xl border-gray-200 hover:bg-brand hover:text-white hover:border-brand transition-all">
              {{ t('search.viewProfile') }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Navigation Blocks -->
  <section v-if="false" class="relative bg-white border-t border-gray-200 py-16">
    <div class="max-w-[1400px] mx-auto px-6">
      <h2 class="text-3xl font-bold text-gray-900 mb-8 text-center">{{ t('sections.exploreMore') }}</h2>
      <NavigationBlocks :search-mode="searchMode" />
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { navigate } from 'vike/client/router';
import { usePageContext } from "vike-vue/usePageContext";
import { ChevronRight, Map as MapIcon } from 'lucide-vue-next';
import { PropertyCard, NavigationBlocks, useAddress, useAPI, Button } from '@repo/ui';
import SearchBox from '@/components/SearchBox.vue';
import { useNavStore, useBrowseAreas } from '@repo/ui'
import type { Building, Page } from '@repo/types'

const { getAddressPart } = useAddress()
const { t, locale } = useI18n();
const pageContext = usePageContext()
const api = useAPI()
const navStore = useNavStore()
const { sortAreas, getBgImage } = useBrowseAreas()

const searchMode = ref<'buy' | 'rent'>(pageContext.mode);

const keywords = computed(() => [
  { label: '千代田区', url: `/${searchMode.value}/tokyo-to/chiyoda-ku/search` },
  { label: '新築マンション', url: `/${searchMode.value}/tags/new-construction?homeType[]=condo` },
  searchMode.value === 'buy' ?
{ label: '渋谷区 3,000万円以内', url: '/buy/tokyo-to/shibuya-ku/search?price=0-30000000&homeType[]=condo' } :
{ label: '渋谷区 15万円以内', url: '/rent/tokyo-to/shibuya-ku/search?price=0-150000&homeType[]=condo' },
  { label: '賃貸 ペット可', url: '/rent/search?labels[]=ペット可' },
])

const topPrefectures = computed(() => [
  { name: '東京都', url: `/${searchMode.value}/tokyo-to` },
  { name: '神奈川県', url: `/${searchMode.value}/kanagawa-ken` },
  { name: '埼玉県', url: `/${searchMode.value}/saitama-ken` },
  { name: '千葉県', url: `/${searchMode.value}/chiba-ken` },
  { name: '大阪府', url: `/${searchMode.value}/osaka-fu` },
  { name: '愛知県', url: `/${searchMode.value}/aichi-ken` },
])

const agents = [
  { id: 1, name: '佐藤 健', company: 'オープンルーム不動産', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop' },
  { id: 2, name: '田中 美咲', company: '東京ミッドタウン支店', avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop' },
  { id: 3, name: '鈴木 一郎', company: '渋谷駅前不動産', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=200&h=200&fit=crop' },
  { id: 4, name: '高橋 留美子', company: '代官山エステート', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop' },
]

// non blocking stuff
const runIdle = (cb: () => void) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(cb)
  } else {
    setTimeout(cb, 0)
  }
}

const recentlyUpdated = ref<Building[]>(pageContext.data.recentlyUpdated?.buildings ?? [])
const latest = ref<Building[]>(pageContext.data.latest?.buildings ?? [])

// watch(pageContext, () => {
//   latest.value = pageContext.data.latest?.buildings
//   page.value = pageContext.data.page
// })

watch(searchMode, () => {
  navStore.setMode(searchMode.value)
  if (pageContext.isClientSide) {
    runIdle(async () => {
      const [{ data: data1 }, { data: data2 }, { data: data3 }] = await Promise.all([
        api.call("buildings", searchMode.value + "/tags/latest", { skip: 0, limit: 6 }),
        api.call("buildings", searchMode.value + "/tags/recently-updated", { skip: 0, limit: 6 }),
        api.call("meta", searchMode.value),
      ])
      if (data1.buildings) latest.value = data1.buildings
      if (data2.buildings) recentlyUpdated.value = data2.buildings
      if (data3) page.value = data3
    })
  }
}, { immediate: true })

const page = ref(pageContext.data.page)
const areas = computed(() => {
  const filtered = page.value?.children
    ?.filter(p => p.level === 'prefecture')
    ?.filter(p => p.stats?.count > 0) || []
  return sortAreas(filtered)
})
</script>
